import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { fmtMoney, fmtDate, STATUS_LABEL, EQUIPMENT_LABEL } from "../lib/format";
import { Search, Filter, Truck } from "lucide-react";

export default function Loads({ board = false }) {
  const navigate = useNavigate();
  const [loads, setLoads] = useState([]);
  const [filters, setFilters] = useState({ q: "", equipment_type: "", pickup_state: "", min_rate: "", max_rate: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = { scope: board ? "board" : "mine" };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get("/loads", { params });
    setLoads(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [board]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">{board ? "Public" : "My"}</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-navy">{board ? "Load Board" : "My Loads"}</h1>
          <p className="text-slate-500 mt-1">{board ? "Browse published loads open for bidding." : "All loads in your queue."}</p>
        </div>
        {!board && (
          <Link to="/app/loads/new" className="btn-primary" data-testid="loads-post-btn">
            <Truck size={16} /> Post load
          </Link>
        )}
      </div>

      <div className="card-base p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input-base pl-9"
              placeholder="Search city or notes"
              value={filters.q}
              onChange={e => setFilters({ ...filters, q: e.target.value })}
              data-testid="filter-search"
            />
          </div>
          <select className="input-base" value={filters.equipment_type}
            onChange={e => setFilters({ ...filters, equipment_type: e.target.value })}
            data-testid="filter-equipment">
            <option value="">All equipment</option>
            {Object.entries(EQUIPMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input className="input-base uppercase" placeholder="Pickup state (e.g., TX)" maxLength={2}
            value={filters.pickup_state}
            onChange={e => setFilters({ ...filters, pickup_state: e.target.value.toUpperCase() })}
            data-testid="filter-state" />
          <button onClick={load} className="btn-secondary" data-testid="filter-apply">
            <Filter size={16} /> Apply
          </button>
        </div>
      </div>

      <div className="card-base overflow-x-auto">
        <table className="table-base">
          <thead>
            <tr>
              <th>Load ID</th><th>Pickup</th><th>Delivery</th><th>Equipment</th>
              <th>Weight</th><th>Distance</th><th>Rate</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-10 text-slate-400">Loading…</td></tr>}
            {!loading && loads.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-400">No loads found.</td></tr>}
            {loads.map(l => (
              <tr key={l.load_id} data-testid={`load-row-${l.load_id}`} onClick={() => navigate(`/app/loads/${l.load_id}`)} className="cursor-pointer">
                <td><span className="font-mono text-xs font-semibold text-navy">{l.load_id}</span></td>
                <td><div className="font-semibold text-slate-900">{l.pickup_city}, {l.pickup_state}</div><div className="text-xs text-slate-500">{fmtDate(l.pickup_time)}</div></td>
                <td><div className="font-semibold text-slate-900">{l.dropoff_city}, {l.dropoff_state}</div><div className="text-xs text-slate-500">{fmtDate(l.delivery_time)}</div></td>
                <td className="capitalize">{EQUIPMENT_LABEL[l.equipment_type] || l.equipment_type}</td>
                <td className="font-mono text-sm">{l.weight?.toLocaleString()} lbs</td>
                <td className="font-mono text-sm">{l.distance} mi</td>
                <td className="font-mono font-bold text-navy">{fmtMoney(l.rate)}</td>
                <td><span className={`badge ${l.status}`}>{STATUS_LABEL[l.status]}</span></td>
                <td><Link to={`/app/loads/${l.load_id}`} className="text-gold font-semibold text-sm hover:underline" data-testid={`load-open-${l.load_id}`}>Open →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
